import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Button, 
  TextField, 
  Rating, 
  Divider,
  Alert
} from '@mui/material';
import { RateReview as FeedbackIcon, Edit as EditIcon } from '@mui/icons-material';
import api from '../services/api';

const SchoolEvaluation = () => {
  const [placement, setPlacement] = useState<any>(null);
  const [evaluation, setEvaluation] = useState<any>(null);
  const [rating, setRating] = useState<number | null>(4);
  const [feedback, setFeedback] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditing, setIsEditing] = useState(false);

  const fetchData = async () => {
    try {
      const pRes = await api.get('/placements/my-placement');
      setPlacement(pRes.data);
      
      const eRes = await api.get(`/school-evaluations/my/${pRes.data.id}`);
      setEvaluation(eRes.data);
      if (eRes.data) {
        setRating(eRes.data.rating);
        setFeedback(eRes.data.feedback || '');
      }
    } catch (err: any) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async () => {
    if (!rating) return;
    try {
      if (isEditing || !evaluation) {
        if (evaluation) {
          await api.put('/school-evaluations', {
            placementId: placement.id,
            rating,
            feedback
          });
          setMessage({ type: 'success', text: 'แก้ไขผลประเมินเรียบร้อยแล้ว' });
        } else {
          await api.post('/school-evaluations', {
            placementId: placement.id,
            rating,
            feedback
          });
          setMessage({ type: 'success', text: 'ส่งผลประเมินโรงเรียนเรียบร้อยแล้ว ขอบคุณสำหรับข้อมูลครับ' });
        }
        setIsEditing(false);
        fetchData();
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'เกิดข้อผิดพลาดในการส่งข้อมูล' });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setMessage({ type: '', text: '' });
  };

  if (!placement) return <Typography sx={{ p: 3 }}>ไม่พบข้อมูลการฝึกประสบการณ์</Typography>;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <FeedbackIcon sx={{ mr: 1, fontSize: { xs: 24, sm: 30 }, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>ประเมินสถานศึกษา (School Evaluation)</Typography>
      </Box>

      {message.text && <Alert severity={message.type as any} sx={{ mb: 3 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}

      <Paper sx={{ p: { xs: 2, sm: 4 }, mb: 4, borderTop: '5px solid #1976d2' }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>ข้อมูลโรงเรียน</Typography>
        <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>{placement.school_name}</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>{placement.school_address}</Typography>
        <Divider sx={{ my: 3 }} />

        {evaluation && !isEditing ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>คุณได้ส่งผลประเมินสำหรับโรงเรียนนี้แล้ว</Alert>
            <Typography variant="subtitle1" gutterBottom>คะแนนที่คุณให้:</Typography>
            <Rating value={evaluation.rating} readOnly size="large" />
            <Box sx={{ mt: 3, p: { xs: 2, sm: 3 }, bgcolor: 'grey.50', borderRadius: 1, border: '1px dashed #ccc' }}>
              <Typography variant="body1" sx={{ fontStyle: 'italic', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                "{evaluation.feedback || 'ไม่มีข้อเสนอแนะเพิ่มเติม'}"
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
              ประเมินเมื่อ: {new Date(evaluation.created_at).toLocaleDateString('th-TH')}
            </Typography>
            <Button 
              startIcon={<EditIcon />} 
              variant="outlined" 
              sx={{ mt: 3 }} 
              onClick={handleEdit}
            >
              แก้ไขการประเมิน
            </Button>
          </Box>
        ) : (
          <>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              ระดับความพึงพอใจต่อการดูแลและสถานที่:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, mb: 4 }}>
              <Rating 
                size="large" 
                value={rating} 
                onChange={(e, val) => setRating(val)} 
              />
              <Typography variant="body2" color="textSecondary">
                ({rating === 5 ? 'ดีเยี่ยม' : rating === 4 ? 'ดีมาก' : rating === 3 ? 'ปานกลาง' : rating === 2 ? 'พอใช้' : 'ควรปรับปรุง'})
              </Typography>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="ข้อเสนอแนะต่อโรงเรียน (เพื่อการพัฒนาในรุ่นถัดไป)"
              placeholder="เช่น การต้อนรับ, ความพร้อมของอุปกรณ์, การให้คำปรึกษาของครูพี่เลี้ยง..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              {isEditing && (
                <Button variant="outlined" size="large" onClick={() => setIsEditing(false)}>
                  ยกเลิก
                </Button>
              )}
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleSubmit}
                disabled={!rating}
                fullWidth={{ xs: true, sm: false } as any}
              >
                {evaluation ? 'บันทึกการแก้ไข' : 'ส่งผลประเมิน'}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
        * ข้อมูลนี้จะถูกนำไปใช้เพื่อพัฒนาคุณภาพการเลือกโรงเรียนในอนาคต ข้อมูลผู้ประเมินจะเป็นความลับ
      </Typography>
    </Box>
  );
};

export default SchoolEvaluation;
